import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ProtectedRoute from './ProtectedRoute';
import { route } from '@/constants/routeName';
import AuthProvider from '@/components/AuthProvider';
import Layout from '@/components/Layout/Layout';
import NotFound from '@/pages/NotFound';
import Home from '@/pages/Home/Home';
import Project from '@/pages/Project/Project';
import Edit from '@/pages/Project/Edit';
import Create from '@/pages/Project/Create';
import User from '@/pages/User/User';
import Users from '@/pages/User/Users';
import TempUsers from '@/pages/User/TempUsers';
import Codes from '@/pages/General/Codes';
import Groups from '@/pages/General/Groups';
import Categories from '@/pages/General/Categories';
import DataSheets from '@/pages/General/DataSheets';
import Downloads from '@/pages/General/Downloads';
import Statuses from '@/pages/General/Statuses';
import Reference from '@/pages/General/Reference';
import UserGuide from '@/pages/General/UserGuide';
import File from '@/pages/File/File';
import Projects from '@/pages/General/Projects';
import { ErrorForwarder } from '@/components/ErrorBoundary/ErrorForwarder';
import ProjectsDetail from '../pages/General/ProjectsDetail';

export default function UseAuthenticatedRoutes() {
	const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
	const user = useSelector((state) => state.auth.user);

	if (!isAuthenticated) return [];
	const r = [
		{
			path: '/',
			element: (
				<ProtectedRoute>
					<AuthProvider>
						<Layout />
					</AuthProvider>
				</ProtectedRoute>
			),
			errorElement: <ErrorForwarder from='auth' />,
			children: [
				{
					path: '/login',
					element: <Navigate to='/' />,
				},
				{
					path: '',
					element: <Home />,
				},
				{
					path: '*',
					element: <NotFound />,
				},
				{
					path: `${route.file}/:number/:name/:id`,
					element: <File />,
				},
				{
					path: `/${route.project}`,
					children: [
						{ index: true, element: <Project /> },
						{
							path: `/${route.project}/:project/:id`,
							element: <Project />,
							privileges: [1, 2, 3, 4, 5, 6],
						},
						{
							path: `/${route.project}/edit/:project`,
							element: <Edit />,
							privileges: [1, 2, 3, 4],
						},
						{
							path: `/${route.project}/duplicate/:id`,
							element: <Edit />,
							privileges: [1, 2, 3, 4],
						},
						{
							path: `/${route.project}/add`,
							element: <Create />,
							privileges: [1, 2, 3, 4],
						},
					],
					privileges: [1, 2, 3, 4, 5, 6],
				},
				{
					path: `/${route.projects}`,
					children: [
						{ index: true, element: <Projects /> },
						{
							path: `/${route.projects}/detail/:name`,
							element: <ProjectsDetail />,
							privileges: [1, 2, 3, 4, 5, 6],
						},
					],
				},
				{
					path: `/${route.user}`,
					element: <User />,
				},
				{
					path: `/${route.users}`,
					element: <Users />,
				},
				{
					path: `/${route.tempUsers}`,
					element: <TempUsers />,
				},
				{
					path: `/${route.codes}`,
					element: <Codes />,
				},
				{
					path: `/${route.groups}`,
					element: <Groups />,
				},
				{
					path: `/${route.categories}`,
					element: <Categories />,
				},
				{
					path: `/${route.dataSheets}`,
					element: <DataSheets />,
				},
				{
					path: `/${route.downloads}`,
					element: <Downloads />,
				},
				{
					path: `/${route.status}`,
					element: <Statuses />,
				},
				{
					path: `/${route.reference}`,
					element: <Reference />,
				},
				{
					path: `/${route.userGuide}`,
					element: <UserGuide />,
				},
			]
				.filter((r) => !r.privileges || r.privileges.includes(user?.privilegelevelid))
				.map((r) => {
					if (r.children) {
						const obj = r;
						const currChild = obj.children.filter((r) => !r.privileges || r.privileges.includes(user?.privilegelevelid));
						return { ...obj, children: currChild };
					}
					return r;
				}),
		},
	];
	return r;
}
